import React, { useState, useEffect, memo, useCallback } from 'react';
import {
  Box,
  Flex,
  Input,
  InputGroup,
} from '@chakra-ui/react';
import { Search } from 'lucide-react';
import Select from '@/components/ui/Select';
import SearchableSelect, { SearchableSelectOption } from '@/components/ui/SearchableSelect';

// Input styles using design tokens
const searchInputStyles = {
  bg: "white",
  borderColor: "gray.300",
  _hover: { borderColor: "brand.400" },
  _focus: { 
    borderColor: "brand.600", 
    // boxShadow: "0 0 0 1px var(--chakra-colors-brand-600)" 
  },
  _dark: { 
    bg: "gray.700",
    borderColor: "gray.600",
    _hover: { borderColor: "brand.600" },
    _focus: { 
      borderColor: "brand.600", 
      // boxShadow: "0 0 0 1px var(--chakra-colors-brand-600)" 
    }
  },
};

const searchIconStyles = {
  color: "gray.400",
  _dark: { color: "gray.500" },
  transition: "color 0.2s",
  "[data-group]:hover &": { color: "brand.600" },
  "[data-group]:focus-within &": { color: "brand.600" },
};

export interface FilterOption {
  value: string;
  label: string;
}

export interface Filter {
  key: string;
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  searchable?: boolean;
  onSearch?: (query: string) => Promise<SearchableSelectOption[]>;
  selectedOption?: SearchableSelectOption | null;
}

interface SearchAndFiltersSectionProps {
  searchValue: string;
  searchPlaceholder?: string;
  onSearchChange: (value: string) => void;
  filters?: Filter[];
  debounceMs?: number;
}

// Isolated SearchInput to prevent re-rendering sibling filters on keystroke
const SearchInput = memo(({ 
  value, 
  onChange, 
  placeholder, 
  debounceMs,
  styles 
}: { 
  value: string; 
  onChange: (value: string) => void;
  placeholder: string;
  debounceMs: number;
  styles: any;
}) => {
  const [localSearchValue, setLocalSearchValue] = useState(value);

  // Sync local state with prop changes
  useEffect(() => {
    setLocalSearchValue(value);
  }, [value]);

  // Debounce search callback
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      onChange(localSearchValue);
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [localSearchValue, onChange, debounceMs]);

  // Memoize input change handler
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchValue(e.target.value);
  }, []);

  return (
    <InputGroup 
      data-group
      startElement={
        <Box {...searchIconStyles}>
          <Search size={16} />
        </Box>
      }
    >
      <Input
        placeholder={placeholder}
        value={localSearchValue}
        onChange={handleInputChange}
        {...styles}
      />
    </InputGroup>
  );
});

SearchInput.displayName = 'SearchInput';

const SearchAndFiltersSection: React.FC<SearchAndFiltersSectionProps> = memo(({
  searchValue,
  searchPlaceholder = "Search...",
  onSearchChange,
  filters = [],
  debounceMs = 300,
}) => {
  return (
    <Box mb={6}>
      <Flex gap={4} align="center" wrap="wrap">
        {/* Search Input */}
        <Box flex="1" minW="250px">
          <SearchInput
            value={searchValue}
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
            debounceMs={debounceMs}
            styles={searchInputStyles}
          />
        </Box>
        {/* Filter Dropdowns */}
        {filters.map((filter) => (
          <Box key={filter.key} minW="200px" maxW="300px" w="fit-content">
            {filter.searchable && filter.onSearch ? (
              <SearchableSelect
                value={filter.value}
                onChange={filter.onChange}
                onSearch={filter.onSearch}
                placeholder={filter.label}
                selectedOption={filter.selectedOption}
                size="sm"
                minSearchLength={0}
                debounceMs={300}
                loadingText="Searching..."
                noResultsText="No matching results"
              />
            ) : (
              <Select
                options={filter.options}
                value={filter.value}
                onChange={filter.onChange}
                placeholder={filter.label}
                size="sm"
                width="full"
              />
            )}
          </Box>
        ))}
      </Flex>
    </Box>
  );
});

SearchAndFiltersSection.displayName = 'SearchAndFiltersSection';

export default SearchAndFiltersSection; 